import React, { useState, useEffect } from "react";
import { createCheckoutOrder, getOrderStatus } from "../services/OrderServices";
import {
  getLocalCart,
  fetchServerCart,
  clearServerCart,
  setLocalCart,
} from "../services/CartServices";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import "./CheckoutPage.css";

function makeMerchantOrderId() {
  return "RN-" + Date.now() + "-" + Math.floor(Math.random() * 9000 + 1000);
}

export default function CheckoutPage() {
  const [order, setOrder] = useState(null);
  const [creating, setCreating] = useState(false);
  const [customer, setCustomer] = useState({
    name: "",
    mobile: "",
    address: "",
    pincode: "",
  });
  const [paid, setPaid] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const navigate = useNavigate();

  const isCombo333 = localStorage.getItem("isCombo333") === "true";
  const isNewYearCombo419 = localStorage.getItem("isNewYearCombo419") === "true";

  useEffect(() => {
    async function loadCart() {
      const token = localStorage.getItem("token");
      if (token) {
        const items = await fetchServerCart();
        setCartItems(
          items.map((item) => ({
            productId: item.productId || item.giftProductId,
            quantity: item.quantity,
            unitPrice: item.unitPrice || 0, // ✅ Use unitPrice from backend
            itemTotal: item.itemTotal || 0, // ✅ Use itemTotal from backend (already includes discounts)
            name: item.Product?.name || item.GiftProduct?.name || "",
          }))
        );
      } else {
        setCartItems(getLocalCart());
      }
    }
    loadCart();
  }, []);

  const handleChange = (e) =>
    setCustomer((s) => ({ ...s, [e.target.name]: e.target.value }));

  // ✅ If combo, fixed ₹333 price
  const cartItemsTotal = () => {
    if (isCombo333 && cartItems.length === 3) return 333;
    if (isNewYearCombo419 && cartItems.length === 4) return 419;

    // ✅ Use itemTotal directly from backend (already has all discounts applied)
    return cartItems
      .reduce(
        (sum, item) => sum + Number(item.itemTotal || 0),
        0
      )
      .toFixed(2);
  };

  const calculateFinalTotal = () => {
    const subtotal = parseFloat(cartItemsTotal());

    // ✅ Delivery FREE for combo users (either combo)
    const deliveryCharge = (isCombo333 || isNewYearCombo419) ? 0 : (subtotal < 100 ? 50 : 0);

    // ✅ Final total fixed for combos
    let finalTotal = subtotal + deliveryCharge;
    if (isCombo333 && cartItems.length === 3) finalTotal = 333;
    if (isNewYearCombo419 && cartItems.length === 4) finalTotal = 419;

    return { subtotal, deliveryCharge, finalTotal };
  };

  // ✅ Calculate total bulk discount applied
  const getTotalBulkDiscount = () => {
    // Backend calculates bulk discount in itemTotal, so we need to show the savings
    // Savings = (unitPrice * quantity) - itemTotal
    return cartItems
      .reduce((sum, item) => {
        const fullPrice = Number(item.unitPrice || 0) * Number(item.quantity || 1);
        const discounted = Number(item.itemTotal || 0);
        return sum + Math.max(0, fullPrice - discounted);
      }, 0)
      .toFixed(2);
  };

  const handleCreateAndPay = async () => {
    if (!customer.name || !customer.mobile || !customer.address || !customer.pincode) {
      toast.error("Please fill all address details");
      return;
    }

    if (!cartItems || cartItems.length === 0) {
      toast.error("Your cart is empty!");
      return;
    }

    try {
      setCreating(true);
      const merchantOrderId = makeMerchantOrderId();
      const { finalTotal } = calculateFinalTotal();
      const amountPaisa = Math.round(finalTotal * 100);

      // 🧾 Build correct payload
      const metadata = {
        udf1: customer.mobile,
        cartItems: cartItems.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
          price: item.price,
        })),
        isCombo333: isCombo333, // ✅ tell backend
        combo333: isCombo333,   // (safety key)
        isNewYearCombo419: isNewYearCombo419,
        combo419: isNewYearCombo419,
        totalAfterDiscount: isCombo333 ? 333 : isNewYearCombo419 ? 419 : finalTotal, // ✅ explicit total
      };

      console.log("📦 Sending metadata:", metadata);

      const created = await createCheckoutOrder(
        customer,
        merchantOrderId,
        amountPaisa,
        metadata
      );

      if (created) {
        setOrder(created);

        if (created.redirectUrl) {
          toast.success("Order created. Redirecting to PhonePe...");
          window.location.href = created.redirectUrl; // ✅ direct redirect (not new tab)
        }
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to create order");
    } finally {
      setCreating(false);
    }
  };

  const pollStatus = async (phonepeOrderId, merchantOrderId) => {
    const interval = setInterval(async () => {
      try {
        const status = await getOrderStatus(phonepeOrderId, merchantOrderId);

        if (status?.success && status.data?.state === "COMPLETED") {
          clearInterval(interval);
          setPaid(true);
          toast.success("Payment successful!");

          try {
            const token = localStorage.getItem("token");
            if (token) await clearServerCart();

            setLocalCart([]);
            setCartItems([]);
            window.dispatchEvent(new Event("cartUpdated"));

            // ✅ Reset Combo Flags
            localStorage.removeItem("isCombo333");
            localStorage.removeItem("isNewYearCombo419");
            localStorage.removeItem("comboProducts");

            toast.info("Cart cleared.");
          } catch (clearErr) {
            console.error("Clear cart error:", clearErr);
          }

          navigate("/orders");
        } else if (status?.data?.state === "FAILED") {
          clearInterval(interval);
          toast.error("Payment failed");
        }
      } catch (err) {
        console.error("Status poll error", err);
      }
    }, 5000);
  };

  const { subtotal, deliveryCharge, finalTotal } = calculateFinalTotal();

  return (
    <div className="rn-checkout">
      <div className="rn-gradient-bg" />
      <motion.div
        className="rn-checkout-card"
        initial={{ opacity: 0, y: 26 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
      >
        <header className="rn-header">
          <h2>Secure Checkout</h2>
          <p>Complete your details & pay securely</p>
        </header>

        {!paid ? (
          <div className="rn-grid">
            <div className="rn-panel">
              <h3>Customer Details</h3>
              <div className="rn-form">
                <input name="name" placeholder="Full Name" onChange={handleChange} />
                <input name="mobile" placeholder="Mobile" onChange={handleChange} />
                <textarea
                  name="address"
                  placeholder="Shipping Address"
                  onChange={handleChange}
                />
                <input
                  name="pincode"
                  placeholder="Pincode"
                  onChange={handleChange}
                />

                <button
                  className="btn rn-btn rn-btn-primary"
                  disabled={creating}
                  onClick={handleCreateAndPay}
                >
                  {creating ? "Processing..." : `Pay ₹${finalTotal} with PhonePe`}
                </button>
              </div>
            </div>

            <div className="rn-panel">
              <h3>Order Summary</h3>
              <div className="rn-summary">
                <div className="row">
                  <span>Subtotal</span>
                  <strong>₹{subtotal}</strong>
                </div>

                {getTotalBulkDiscount() > 0 && (
                  <div className="row" style={{ color: "#27ae60" }}>
                    <span>Bulk Discount</span>
                    <strong>-₹{getTotalBulkDiscount()}</strong>
                  </div>
                )}

                {!isCombo333 && deliveryCharge > 0 && (
                  <div className="row">
                    <span>Delivery Charge</span>
                    <strong>₹{deliveryCharge}</strong>
                  </div>
                )}

                <div className="row total">
                  <span>Total Payable</span>
                  <strong>₹{finalTotal}</strong>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center">
            <h3>🎉 Payment Successful!</h3>
            <button className="btn rn-btn rn-btn-primary" onClick={() => navigate("/")}>
              Go Home
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
